import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getRemainVacation,
  getVacationDetail,
  getVacationList,
  getVacationTypes,
  saveVacation,
  getVacationProposeList,
  postVacationProposeList,
  getHolidayList,
  postHolidayList,
  getRemainVacationByEnumber,
  putVacationApprovalDone,
  putVacationApprovalReturn,
  getVacationSearchFilter,
  getVacationEventType,
  getVacationCountInfo,
  getVacationCheckApprover,
  postVacationAllList,
  getVacationDeleteByIdx,
  saveVacationFile,
  getVacationFile,
  deleteAdminVacationByIdx,
} from '@entities/vacation/api/vacation';

/**
 * useRemainVacationQuery
 * - 남은 휴가 데이터를 조회하는 React Query 훅
 * @returns {UseQueryResult<Object>}
 */
function useRemainVacationQuery() {
  return useQuery({
    queryKey: ['remainVacation'],
    queryFn: getRemainVacation,
    select: (data) => data?.resultData ?? null,
    // LNB 재진입 시 휴가 현황 최신화
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
/**
 * useSaveVacationMutation
 * - 휴가 신청 또는 휴가 수정 요청을 수행하는 React Query Mutation 훅
 * - params 는 컴포넌트 또는 호출부에서 전달받아 saveVacation 에 그대로 전달됨
 * @returns {UseMutationResult} 휴가 신청 Mutation 결과 및 상태 정보
 */
function useSaveVacationMutation() {
  return useMutation({
    mutationFn(params) {
      return saveVacation(params);
    },
  });
}

/**
 * useVacationDetailQuery
 * - 휴가 상세 조회 React Query 훅
 * @param {number|string} idx - 휴가 문서 ID
 * @returns {UseQueryResult<Object>}
 */
function useVacationDetailQuery(idx) {
  return useQuery({
    queryKey: ['vacationDetail', idx],
    queryFn: () => getVacationDetail(idx),
    select: (data) => data?.resultData ?? null,
    enabled: Boolean(idx), // idx 가 없을 때는 요청 안 보냄
  });
}

/**
 * useVacationListQuery
 * - 특정 연도의 휴가 신청 내역을 조회하는 React Query 훅
 * - resultData.docInfoList 만 반환해 화면에서 바로 사용 가능
 * @param {number|string} year
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<Array>}
 */
function useVacationListQuery(year, options = {}) {
  return useQuery({
    queryKey: ['vacationList', year],
    queryFn: () => getVacationList(year),
    select: (data) => data?.resultData?.docInfoList ?? [],
    // 화면 재진입·탭 전환 시 최신 목록
    staleTime: 0,
    refetchOnMount: 'always',
    ...options,
  });
}

/**
 * useVacationTypesQuery
 * - 휴가 종류 리스트를 조회하는 React Query 훅
 */
function useVacationTypesQuery() {
  return useQuery({
    queryKey: ['vacationTypes'],
    queryFn: getVacationTypes,
    select: (data) =>
      Array.isArray(data?.resultData?.vacationTypeList) ? data.resultData.vacationTypeList : [],
  });
}

/**
 * useGetVacationProposeListQuery
 * - 휴가 결재 목록 조회 (결재자가 결재해야 하는 휴가 목록)
 * - resultData.docInfoList 만 반환
 * @param {string} startDate - 시작일
 * @param {string} endDate - 종료일
 * @param {number} stateCode - 상태 코드 (선택)
 * @param {number} vacationType - 휴가 구분 코드 (선택)
 * @param {number} teamCode - 기안부서 (선택)
 * @param {number} name - 기안자 (선택)
 */
function useGetVacationProposeListQuery(
  startDate,
  endDate,
  stateCode,
  vacationType,
  teamCode,
  name,
) {
  return useQuery({
    queryKey: ['vacationProposeList', startDate, endDate, stateCode, vacationType, teamCode, name],
    queryFn: () =>
      getVacationProposeList({
        startDate,
        endDate,
        stateCode,
        vacationType,
        teamCode,
        name,
      }),
    select: (data) => data?.resultData?.docInfoList ?? [],
    // 화면 재진입 시 최신 결재 목록
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: !!startDate && !!endDate,
  });
}

/**
 * 휴가 결재 목록 조회 (POST)
 * @param {String} startDate
 * @param {String} endDate
 * @param {Number} stateCode
 * @param {Number} teamCode
 * @param {String} name
 * @param {Number} vacationType
 */
function usePostVacationProposeListQuery(
  startDate,
  endDate,
  stateCode,
  teamCode,
  name,
  vacationType,
) {
  return useQuery({
    queryKey: [
      'vacationProposeListPost',
      startDate,
      endDate,
      stateCode,
      teamCode,
      name,
      vacationType,
    ],
    queryFn: () =>
      postVacationProposeList({
        startDate,
        endDate,
        stateCode,
        teamCode,
        name,
        vacationType,
      }),
    enabled: !!startDate && !!endDate,
    // 결재 목록 1분간 캐싱
    staleTime: 1000 * 60 * 1,
  });
}

/**
 * useGetHolidayListQuery
 * - 특정 기간 내 휴일 조회
 * @param {Object} params - { startDate, endDate }
 */
function useGetHolidayListQuery(startDate, endDate) {
  return useQuery({
    queryKey: ['vacationHolidayList', startDate, endDate],
    queryFn: () =>
      getHolidayList({
        startDate,
        endDate,
      }),
    enabled: !!startDate && !!endDate,
  });
}

/**
 * usePostHolidayListQuery
 * - 특정 기간 내 휴일 조회(post)
 * @param {String} startDate
 * @param {String} endDate
 */
function usePostHolidayListQuery(startDate, endDate) {
  return useQuery({
    queryKey: ['vacationHolidayListPost', startDate, endDate],
    queryFn: () =>
      postHolidayList({
        startDate,
        endDate,
      }),
    enabled: !!startDate && !!endDate,
  });
}
/**
 * useGetRemainVacationByEnumberQuery
 * 잔여 휴가 기간 조회(특정사원)
 * @param {number} enumber - 잔여 휴가를 조회할 사원번호
 */
function useGetRemainVacationByEnumberQuery(enumber, options = {}) {
  return useQuery({
    queryKey: ['RemainVacationByEnumber', enumber],
    queryFn: () => getRemainVacationByEnumber(enumber),
    enabled: Boolean(enumber), // idx 가 없을 때는 요청 안 보냄
    ...options,
  });
}

/**
 * usePutVacationApprovalDoneMutation
 * 휴가 결재 처리
 *  @param {object} options - React Query 옵션(onSuccess 등)
 */
function usePutVacationApprovalDoneMutation(options = {}) {
  return useMutation({
    /**
     * mutationFn
     * @param {{ idx: number, comment: string }} params
     */
    mutationFn({ idx, comment }) {
      return putVacationApprovalDone(idx, comment);
    },
    ...options,
  });
}
/**
 * usePutVacationApprovalReturnMuration
 * 휴가 반려 처리
 * @param {object} options - React Query 옵션(onSuccess 등)
 */
function usePutVacationApprovalReturnMuration(options = {}) {
  return useMutation({
    /**
     * mutationFn
     * @param {{ idx: number, comment: string }} params
     */
    async mutationFn({ idx, comment }) {
      const res = await putVacationApprovalReturn(idx, comment);

      //  업무 실패를 에러로 승격
      if (res?.result_code !== 0) {
        throw new Error(res?.error_msg || '반려 처리에 실패했습니다.');
      }

      return res;
    },
    ...options,
  });
}

/**
 * 휴가 검색 필터 조회
 */
function useGetVacationSearchFilterQuery(options = {}) {
  return useQuery({
    queryKey: ['vacationSearchFilter'],
    queryFn: () => getVacationSearchFilter(),
    ...options,
  });
}
/**
 * 경조 타입 조회
 * @param {number} eventTypeCode : 휴가타입 코드
 */
function useGetVacationEventType(eventTypeCode, options = {}) {
  return useQuery({
    queryKey: ['vacationEventType', eventTypeCode],
    queryFn: () => getVacationEventType(eventTypeCode),
    enabled: Boolean(eventTypeCode), // idx 가 없을 때는 요청 안 보냄
    ...options,
  });
}

/**
 * 휴가함 카운터 정보 요청
 */
function useGetVacationCountInfo(options = {}) {
  return useQuery({
    queryKey: ['vacationCountInfo'],
    queryFn: () => getVacationCountInfo(),
    ...options,
  });
}
/**
 * 상위 결재자 정보 조회 훅
 */
function useGetVacationCheckApprover(options = {}) {
  return useQuery({
    queryKey: ['vacationCheckApprover'],
    queryFn: () => getVacationCheckApprover(),
    ...options,
  });
}

/**
 * useGetVacationAllList
 * - 특정 회사의 전체 휴가 리스트를 POST 방식으로 조회하는 React Query 훅
 *
 * @param {string} name - 검색할 직원 이름
 * @param {string} startDate - 조회 시작일 (YYYY-MM-DD)
 * @param {string} endDate - 조회 종료일 (YYYY-MM-DD)
 * @param {number} stateCode - 휴가 상태 코드 (예: 1=결재중)
 * @param {number} teamCode - 부서(팀) 코드
 * @param {number} vacationType - 휴가 타입 코드
 * @param {object} options - React Query 옵션 (enabled, staleTime 등)
 *
 * @returns {UseQueryResult} React Query의 Query 결과 객체
 */
function usePostVacationAllListQuery(
  name,
  startDate,
  endDate,
  stateCode,
  teamCode,
  vacationType,
  options = {},
) {
  return useQuery({
    queryKey: [
      'admin',
      'vacation',
      'list',
      name,
      startDate,
      endDate,
      stateCode,
      teamCode,
      vacationType,
    ],
    queryFn: () => postVacationAllList(name, startDate, endDate, stateCode, teamCode, vacationType),
    ...options,
  });
}

/**
 * 등록한 휴가 삭제
 * @param {number} idx : 휴가 고유 번호
 */
function useGetVacationDeleteByIdxMutation(options = {}) {
  return useMutation({
    mutationFn({ idx }) {
      return getVacationDeleteByIdx(idx);
    },
    ...options,
  });
}

/**
 * 휴가 첨부파일 등록
 */
function useSaveVacationFileMutation(options = {}) {
  return useMutation({
    mutationFn: async ({ file, vacationIdx }) => {
      if (!file) throw new Error('첨부파일이 없습니다.');
      if (!vacationIdx) throw new Error('vacationIdx가 없습니다.');
      const formData = new FormData();
      formData.append('file', file);

      const response = await saveVacationFile(formData, vacationIdx);
      return response;
    },
    ...options,
  });
}

/**
 * 휴가 첨부파일 다운로드
 * (Blob 방식 대신 정적 URL 방식으로 변경하여 모바일 호환성 강화)
 */
function useGetVacationFile(options = {}) {
  return useMutation({
    mutationFn: async ({ fileIdx, fileURL: directURL, fileName: directName }) => {
      let fileURL = directURL;
      let fileName = directName || 'download';

      if (!fileURL) {
        if (!fileIdx) {
          throw new Error('fileIdx 또는 fileURL이 없습니다.');
        }

        /* =========================
         * 1️⃣ 파일 메타데이터(JSON) 및 URL 조회
         ========================= */
        const response = await getVacationFile(fileIdx);

        if (response && response.result_code === 0 && response.resultData) {
          fileURL = response.resultData.fileURL;
          fileName = response.resultData.fileName || fileName;
        } else {
          throw new Error(response?.error_msg || '파일 정보를 가져오는데 실패했습니다.');
        }

        if (!fileURL) {
          throw new Error('파일 다운로드 URL을 찾을 수 없습니다.');
        }
      }

      return {
        fileURL,
        fileName,
      };
    },

    onSuccess: ({ fileURL, fileName }, variables) => {
      /* =========================
       * 2️⃣ 브라우저/Webview에서 다운로드 트리거
       * apiClient/axios 대신 <a> 태그를 사용하여 직접 다운로드
       ========================= */
      const a = document.createElement('a');

      // baseUrl 처리: 외부 도메인일 경우 또는 절대 경로인 경우 대응
      const fullURL = fileURL.startsWith('http') ? fileURL : `${window.location.origin}${fileURL}`;

      a.href = fullURL;
      a.download = fileName;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (typeof options.onSuccess === 'function') {
        options.onSuccess({ fileName }, variables);
      }
    },

    onError: (err, variables) => {
      console.error('다운로드 실패:', err);

      if (typeof options.onError === 'function') {
        options.onError(err, variables);
      }
    },

    ...options,
  });
}

/**
 * 관리자 휴가 삭제 Mutation
 * - 관리자 권한으로 휴가 상태를 삭제(상태 7로 변경)
 * @param {object} options - React Query 옵션 (onSuccess, onError 등)
 */
function useDeleteAdminVacationMutation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn({ idx }) {
      if (!idx) throw new Error('idx는 필수 값입니다.');
      return deleteAdminVacationByIdx(idx);
    },

    onSuccess: (...args) => {
      /**
       * 관리자 휴가 리스트 무효화
       * - 정확한 key 사용
       */
      queryClient.invalidateQueries({
        queryKey: ['admin', 'vacation', 'list'],
      });

      // 외부 onSuccess도 존중
      if (typeof options.onSuccess === 'function') {
        options.onSuccess(...args);
      }
    },

    onError: options.onError,
  });
}
export {
  useRemainVacationQuery,
  useSaveVacationMutation,
  useVacationDetailQuery,
  useVacationListQuery,
  useVacationTypesQuery,
  useGetVacationProposeListQuery,
  usePostVacationProposeListQuery,
  useGetHolidayListQuery,
  usePostHolidayListQuery,
  useGetRemainVacationByEnumberQuery,
  usePutVacationApprovalDoneMutation,
  usePutVacationApprovalReturnMuration,
  useGetVacationSearchFilterQuery,
  useGetVacationEventType,
  useGetVacationCountInfo,
  useGetVacationCheckApprover,
  usePostVacationAllListQuery,
  useGetVacationDeleteByIdxMutation,
  useSaveVacationFileMutation,
  useGetVacationFile,
  useDeleteAdminVacationMutation,
};
